import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  Input,
} from '@angular/core';
import {
  switchMap,
  takeUntil,
  Subject,
  tap,
  catchError,
  EMPTY,
  of,
  Observable,
  map,
  startWith,
  shareReplay,
  take,
} from 'rxjs';

import { Router, RouterModule } from '@angular/router';
import { WhoMobileComponent } from '@app/who-mobile/who-mobile.component';
import { WhereMobileComponent } from '@app/where-mobile/where-mobile.component';
import { WhenMobileComponent } from '@app/when-mobile/when-mobile.component';
import { AngularSvgIconModule } from 'angular-svg-icon';
import {
  Address,
  ApiService,
  Currency,
  CityRef,
} from '@app/services/api/api.service';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { LocaleService } from '@app/locale.service';
import { EnvironmentService } from '@app/environment.service';
import { PlaceRef, SearchSuggestion } from '@app/explore/places.service';
import { HttpParams } from '@angular/common/http';
import { HostelSearchParams, SearchService } from '@app/search.service';

@Component({
  selector: 'app-mobile-explore-page',
  standalone: true,
  imports: [
    NgOptimizedImage,
    AngularSvgIconModule,
    RouterModule,
    AngularSvgIconModule,
    ReactiveFormsModule,
    WhenMobileComponent,
    WhoMobileComponent,
    WhereMobileComponent,
    CommonModule,
  ],
  templateUrl: './mobile-explore-page.component.html',
  styleUrls: ['./mobile-explore-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileExplorePageComponent {
  public exploring = false;
  public searchForm!: FormGroup;

  @Output()
  public propertiesChange = new EventEmitter<any[]>();

  @Output()
  public exploreClicked = new EventEmitter<object>(); // handled by CityStaysComponent

  @Input()
  public where: Address;

  public activeInput: string | undefined;

  public search = new Subject<void>();

  private destroyed = new Subject<void>();

  public pwaUrl: Observable<string>;

  public currency: Observable<Currency>;
  public searchParams: Observable<HostelSearchParams>;

  constructor(
    private readonly apiService: ApiService,
    private localeService: LocaleService,
    private readonly environment: EnvironmentService,
    private readonly router: Router,
    private readonly searchService: SearchService
  ) {}

  public explore(input: string) {
    this.exploring = true;
    this.activeInput = input;
  }

  ngOnInit() {
    this.searchParams = this.searchService.searchParams$;
    this.currency = this.localeService.currentCurrency;

    this.searchForm = new UntypedFormGroup({
      where: new FormControl<PlaceRef | CityRef | Address | null>(
        this.where || null
      ),
      when: new UntypedFormControl('', Validators.required),
      who: new UntypedFormControl(1, Validators.required),
    });

    this.searchForm.valueChanges
      .pipe(
        tap((value) => {
          console.log(value);
          if (this.activeInput === 'where' && !value.when) {
            // only do if no when yet
            this.activeInput = 'when';
          }
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();

    this.search
      .pipe(
        // filter?
        //  value.when && (value.where || true) && value.who;
        switchMap((_) => {
          const value = this.searchForm.getRawValue();
          return this.apiService
            .searchHostels({
              cityId: value.where.cityId,
              searchKey: value.where.city || value.where.cityName,
              checkInDate: value.when[0],
              checkOutDate: value.when[1],
              noOfGuests: value.who,
            })
            .pipe(
              catchError((error) => {
                return of(EMPTY);
              })
            );
        }),
        takeUntil(this.destroyed)
      )
      .subscribe((resp) => {
        if (resp?.data?.data) {
          this.apiService.properties.next(resp.data.data);
          this.router.navigate(['/']);
        }
      });

    this.pwaUrl = this.searchForm.valueChanges.pipe(
      map((value) => {
        const params = {};

        if (value.where) {
          if (value.where.cityId) {
            params['city_id'] = value.where.cityId;
          }
          if (value.where.stateId) {
            params['state_id'] = value.where.stateId;
          }
          if (value.where.countryId) {
            params['country_id'] = value.where.countryId;
          }
          params['search_key'] = this.getPlaceSearchKey(value.where);
          params['search_display_text'] = this.getPlaceSearchDisplayText(
            value.where
          );
          params['search_type'] = value.where.searchType;
        } else {
          params['search_key'] = 'anywhere';
          params['search_type'] = 7;
          params['search_display_text'] = 'Anywhere';
        }

        if (value.when) {
          params['start'] = value.when[0];
          params['end'] = value.when[1];
        }

        if (value.who) {
          params['number_of_guests'] = value.who;
        }

        // add currency
        this.currency.pipe(take(1)).subscribe((currency) => {
          params['currency_code'] = currency.code;
          params['currency_symbol'] = currency.symbol_native;
        });

        const queryString = new HttpParams({ fromObject: params });

        return `${this.environment.pwaUrl}?${queryString.toString()}`;
      }),

      startWith(this.environment.pwaUrl),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      })
    );

    this.pwaUrl.pipe(takeUntil(this.destroyed)).subscribe();
  }

  public isHostel(val: SearchSuggestion): boolean {
    if (!val) {
      return false;
    }

    console.log(val);

    return val.searchType === 2;
  }

  doSearch() {
    this.search.next();
  }

  clear() {
    this.searchForm.reset();
    this.activeInput = 'where';
  }

  ngOnDestroy() {
    this.destroyed.next();
    this.destroyed.complete();
  }

  getPlaceSearchDisplayText(where: any) {
    return where.city
      ? `${where.city}, ${where.country}`
      : where.state
      ? `${where.state}, ${where.country}`
      : where.country;
  }

  getPlaceSearchKey(where: any) {
    return where.city ?? where.cityName ?? where.state ?? where.country;
  }   
}
